import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { GifList, IUserList } from "./GifList";
import { useSearchMyGifs } from "queries";
import { useQuery } from "hooks/useQuery";

import "./UserGifList.scss";
import { IGif, INicheMap } from "services/types";
import { filterGifsList } from "utils/filterGifsList";
import { filterNichesList } from "utils/filterNichesList";
import { filterUsersList } from "utils/filterUsersList";
import { routes } from "routes";
import { ViewRegion } from "utils/metrics";

export const MyGifList = ({ id }: { id: string }) => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [totalGifs, setTotalGifs] = useState<number>(0);
  const [gifs, setGifs] = useState<IGif[]>([]);
  const [users, setUsers] = useState<IUserList>({});
  const [topic, setTopic] = useState<string>("a");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [order, setOrder] = useState("new");
  const [tiles, setTiles] = useState<boolean>(true);
  const [niches, setNiches] = useState<INicheMap>({});
  const navigate = useNavigate();
  const query = useQuery();

  const { data, isSuccess, isLoading, isError } = useSearchMyGifs({
    type: topic === "a" ? undefined : topic,
    order,
    page: page,
    tags: selectedTags.join(",")
  });

  const reload = useCallback(() => {
    setTotalGifs(0);
    setGifs([]);
    setPage(1);
    window.scrollTo(0, 0);
  }, []);

  const changeTopic = useCallback((value: string) => {
    console.debug(`[MyGifList] topic changed to ${value}`);
    setTopic(value);
    reload();
  }, [reload]);

  const changeOrder = useCallback((value: string) => {
    console.debug(`[MyGifList] order changed to ${value}`);
    navigate(routes.user(id, value));
    setOrder(value);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const changeTags = useCallback((value: string) => {
    setSelectedTags(prev => {
      const copyTags = [...prev];
      const isTagIndex = prev.indexOf(value);

      if (isTagIndex >= 0) {
        copyTags.splice(isTagIndex, 1);
      } else {
        copyTags.push(value);
      }

      return copyTags;
    });

    reload();
  }, [reload]);

  useEffect(() => {
    if (isSuccess && data !== undefined) {
      setGifs(prev => filterGifsList(prev, data.gifs));
      setPages(data.pages);
      setTotalGifs(data.total);
      setUsers(prev => filterUsersList(prev, data.users));

      if (data.niches) {
        setNiches(prev => filterNichesList(prev, data.niches));
      }
    }
  }, [data, isSuccess]);

  useEffect(() => {
    const order = query.get("order");

    if (order) {
      changeOrder(order);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeMode = useCallback((mode: boolean) => {
    console.debug(`[MyGifList] tiles changed to ${mode}`);
    setTiles(mode);
  }, []);

  const fetchMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  return (
    <GifList
      className="userGifList"
      ordering
      topic={topic}
      order={order}
      tiles={tiles}
      gifs={gifs}
      users={users}
      niches={niches}
      userID={id}
      totalLength={totalGifs}
      selectedTags={selectedTags}
      hasMore={page < pages}
      changeTopic={changeTopic}
      changeOrder={changeOrder}
      changeMode={changeMode}
      changeTags={changeTags}
      fetchMore={fetchMore}
      isError={isError}
      isLoading={isLoading}
      source={ViewRegion.Profile}
      rel={`user:${id}`}
    />
  );
};
